import { useEffect, useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";

import {
  categoriesPageModeAtom,
  filterInfoAtom,
  selectedFiltersAtom,
  appliedFiltersAtom,
  openStickyFilterAtom,
  openMobileFilterAtom,
} from "../atom/listings";
import { selectedFiltersFromQueryParams } from "../utils/selectedFiltersFromQueryParams";
import {
  openMobileFilterAtom as productsOpenMobileFilterAtom,
  filterInfoAtom as productsFiltersInfoAtom,
  selectedFiltersAtom as productsSelectedFiltersAtom,
  appliedFiltersAtom as productsAppliedFiltersAtom,
  openStickyFilterAtom as productsOpenStickyFilterAtom,
} from "../atom/products";
import useSearchParamsHandler from "./useSearchParamsHandler";
import { transformDataWithId } from "../utils/transformDataWithId";

const initFilter = {
  bw: [],
  vid: [],
  brnd: [],
  sf: [1, 2],
  bullet_type_s: [],
  bt: [],
  shell_length: [],
  mt: [],
  rpb: [],
};

const useSelectedFilter = () => {
  const location = useLocation();
  const [categoriesPageMode] = useAtom(categoriesPageModeAtom);

  const isListingsMode = categoriesPageMode === "listings";
  const [selectedFilters, setSelectedFilters] = useAtom(
    isListingsMode ? selectedFiltersAtom : productsSelectedFiltersAtom
  );
  const [appliedFilters, setAppliedFilters] = useAtom(
    isListingsMode ? appliedFiltersAtom : productsAppliedFiltersAtom
  );
  const [openStickyFilter, setOpenStickyFilter] = useAtom(
    isListingsMode ? openStickyFilterAtom : productsOpenStickyFilterAtom
  );
  const [, setOpenMobileFilter] = useAtom(
    isListingsMode ? openMobileFilterAtom : productsOpenMobileFilterAtom
  );
  const [filterInfo] = useAtom(
    isListingsMode ? filterInfoAtom : productsFiltersInfoAtom
  );
  // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams();
  const { params, clearParams } = useSearchParamsHandler();

  const queryParamsArr = Object.entries(params);

  const queryString = queryParamsArr.map((item) => {
    return {
      title: item[0],
      value: item[1],
    };
  });

  useEffect(() => {
    const newFilters = selectedFiltersFromQueryParams(queryString, filterInfo);
    setSelectedFilters(newFilters);
    setAppliedFilters(newFilters);
    // eslint-disable-next-line
  }, [location.search]);

  const AddOrUpdateFilter = useCallback(
    (key, value, type) => {
      setSelectedFilters((prev) => {
        const currentValues = prev[key] ?? [];

        let newValues = currentValues;
        if (type === "add") {
          const exists = currentValues.some(
            (v) => (v.flag || v.value) === value
          );

          if (!exists) {
            if (key === "bw") {
              if (value.includes("-")) {
                const bwValues = value.split("-");
                newValues = [
                  ...currentValues,
                  {
                    title: key,
                    value: `${bwValues?.[0]} - ${bwValues?.[1]}`,
                    flag: value,
                  },
                ];
              } else {
                const bwFilter = transformDataWithId(
                  filterInfo,
                  [{ title: key, value: `${value}` }],
                  "bullet_weight",
                  "name",
                  "bw",
                  "alt_name"
                );
                newValues = [...currentValues, bwFilter[0]];
              }
            } else if (key === "brnd") {
              const brndFilter = transformDataWithId(
                filterInfo,
                [{ title: key, value: `${value}` }],
                "brand",
                "brand_name",
                "brnd",
                "brand_id"
              );
              newValues = [...currentValues, brndFilter[0]];
            } else if (key === "vid") {
              const vidFilter = transformDataWithId(
                filterInfo,
                [{ title: key, value: `${value}` }],
                "vendor_name",
                "vendor_name",
                "vid",
                "vendor_id"
              );
              newValues = [...currentValues, vidFilter[0]];
            } else if (key === "mt") {
              const mtFilter = transformDataWithId(
                filterInfo,
                [{ title: key, value: `${value}` }],
                "material",
                "name",
                "mt",
                "id"
              );
              newValues = [...currentValues, mtFilter[0]];
            } else if (key === "bullet_type_s") {
              const mtFilter = transformDataWithId(
                filterInfo,
                [{ title: key, value: `${value}` }],
                "bullet_type_s",
                "name",
                "bullet_type_s",
                "id"
              );
              newValues = [...currentValues, mtFilter[0]];
            } else if (key === "bt") {
              const mtFilter = transformDataWithId(
                filterInfo,
                [{ title: key, value: `${value}` }],
                "bullet_type",
                "name",
                "bt",
                "id"
              );
              newValues = [...currentValues, mtFilter[0]];
            } else
              newValues = [...currentValues, { title: key, value: `${value}` }];
          } else {
            newValues = currentValues;
          }
        } else if (type === "remove") {
          newValues = currentValues.filter(
            (v) => (v.flag || v.value) + "" !== value
          );
        } else if (type === "replace") {
          if (key === "bw") {
            if (value.includes("-")) {
              const bwValues = value.split("-");
              newValues = [
                {
                  title: key,
                  value: `${bwValues?.[0]} - ${bwValues?.[1]}`,
                  flag: value,
                },
              ];
            }
          } else newValues = [{ title: key, value: `${value}`, flag: value }];
        }

        return {
          ...prev,
          [key]: newValues,
        };
      });
      setOpenStickyFilter(true);
    },
    [setSelectedFilters, setOpenStickyFilter, filterInfo]
  );

  const ClearFilter = useCallback(() => {
    setSelectedFilters(initFilter);
    setAppliedFilters(initFilter);
    setOpenStickyFilter(false);
    setOpenMobileFilter(false);
    clearParams();
  }, [
    setSelectedFilters,
    clearParams,
    setAppliedFilters,
    setOpenStickyFilter,
    setOpenMobileFilter,
  ]);

  const ApplyFilter = useCallback(() => {
    const newParams = { ...params };

    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length === 0) delete newParams[key];
        value.map((item, index) => {
          if (
            typeof item === "object" &&
            item !== null &&
            "title" in item &&
            "value" in item
          ) {
            let existingValues = newParams[key]
              ? newParams[key].split(",")
              : [];
            if (index === 0) existingValues = [];

            existingValues.push(item.flag || item.value);

            if (
              (item.value + "").includes("-") &&
              (key === "bw" || key === "rpb")
            ) {
              newParams.page = "1";
              newParams.sort_by = "landed_cpr";
              newParams[key] = item.flag || item.value;
            } else if (key === "page") {
              newParams[key] = item.value;
            } else {
              newParams.page = "1";
              newParams.sort_by = "landed_cpr";
              newParams[key] = existingValues.join(",");
            }
          }

          return newParams;
        });
      }
    });
    setSearchParams(newParams, { replace: true });
    setOpenStickyFilter(false);
    setOpenMobileFilter(false);
  }, [
    selectedFilters,
    setSearchParams,
    setOpenStickyFilter,
    params,
    setOpenMobileFilter,
  ]);

  const selectFilter = (filter) => {
    AddOrUpdateFilter(filter.title, filter.flag || filter.value, "replace");
  };

  const removeRangeFilter = (filter) => {
    setSelectedFilters((prev) => {
      return {
        ...prev,
        [filter.title]: [],
      };
    });
  };

  return {
    openStickyFilter,
    selectFilter,
    removeRangeFilter,
    appliedFilters,
    selectedFilters,
    AddOrUpdateFilter,
    ClearFilter,
    ApplyFilter,
  };
};

export default useSelectedFilter;
